import { clientIdentity } from '@/context/ApiClientContext/identity';
import { getHttpErrorMessage as message } from '@/services/httpRetry';
import classes from './ForgeAccess.module.css';
import { useEffect, useRef, useState } from 'react';
import { Alert, Container, Group, Stack, Table, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useApiClient } from '@/context/ApiClientContext/useApiClient';
import StyledButton from '@/components/StyledButton/StyledButton';
import StyledSelect from '@/components/StyledInputFields/StyledSelect';

type Choice = { id: string; name?: string; email?: string; org_id?: string; org_name?: string };
type Role = 'none' | 'viewer' | 'editor';
type Edit = { role: Role; revision: string };
type AccessRow = { id: string; name: string; role: Role; revision: string };
type Context = { user_id: string; email: string; environment: string };
const options = { suppressGlobalErrorToast: true, handleForbiddenLocally: true };
// Keep keyboard-highlighted options neutral in this administrative surface.
const selectStyles = {
  dropdown: {
    '--mantine-primary-color-filled': 'var(--mantine-color-default-hover)',
  },
  option: { color: 'var(--mantine-color-text)' },
};
function mergeChoices(previous: Choice[], incoming: Choice[]) {
  const choices = new Map(previous.map((choice) => [choice.id, choice]));
  for (const choice of incoming) {
    choices.set(choice.id, { ...choices.get(choice.id), ...choice });
  }
  return [...choices.values()];
}
export default function ForgeAccessPage() {
  const api = useApiClient();
  const { projectId: routeProject } = useParams();
  return (
    <AccessPage key={`${clientIdentity(api)}:${routeProject ?? ''}`} routeProject={routeProject} />
  );
}

function AccessPage({ routeProject }: { routeProject?: string }) {
  const api = useApiClient();
  const choicesController = useRef<AbortController | null>(null);
  const [editing, setEditing] = useState(false);
  const [project, setProject] = useState(routeProject ?? '');
  const [subject, setSubject] = useState('');
  const [projects, setProjects] = useState<Choice[]>([]);
  const [selectedProject, setSelectedProject] = useState<Choice>();
  const [users, setUsers] = useState<Choice[]>([]);
  const [projectOffset, setProjectOffset] = useState<number | null>(0);
  const [userPage, setUserPage] = useState<number | null>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const context = useQuery({
    meta: { suppressGlobalErrorToast: true },
    queryKey: ['forge-access-context', clientIdentity(api)],
    queryFn: async () => (await api.get<Context>('/internal/forge-access/context', options)).data,
    retry: false,
  });
  const callerId = context.data?.user_id;
  useEffect(() => {
    setSubject(callerId ?? '');
  }, [callerId]);
  useEffect(() => {
    setUsers([]);
    setProjects([]);
    setError('');
    setProjectOffset(null);
    setUserPage(null);
    if (!callerId) return;
    const controller = new AbortController();
    choicesController.current = controller;
    setLoading(true);
    void Promise.all([
      api.get('/internal/forge-access/projects', { ...options, signal: controller.signal }),
      api.get('/internal/forge-access/users', { ...options, signal: controller.signal }),
    ])
      .then(([projectsResponse, usersResponse]) => {
        if (controller.signal.aborted) return;
        setProjects(projectsResponse.data.results);
        setUsers(usersResponse.data.results);
        setProjectOffset(projectsResponse.data.next_offset);
        setUserPage(usersResponse.data.next_page);
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(message(err));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => {
      controller.abort();
      if (choicesController.current === controller) choicesController.current = null;
    };
  }, [callerId, api]);
  async function loadChoices() {
    const controller = choicesController.current;
    if (!controller || controller.signal.aborted) return;
    setLoading(true);
    setError('');
    try {
      if (projectOffset !== null) {
        const { data } = await api.get('/internal/forge-access/projects', {
          ...options,
          signal: controller.signal,
          params: { offset: projectOffset },
        });
        if (controller.signal.aborted) return;
        setProjects((old) => mergeChoices(old, data.results));
        setProjectOffset(data.next_offset);
      }
      if (userPage !== null) {
        const { data } = await api.get('/internal/forge-access/users', {
          ...options,
          signal: controller.signal,
          params: { page: userPage },
        });
        if (controller.signal.aborted) return;
        setUsers((old) => mergeChoices(old, data.results));
        setUserPage(data.next_page);
      }
    } catch (err) {
      if (!controller.signal.aborted) setError(message(err));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }
  return (
    <Container size="lg" py="xl">
      <Stack>
        <Title order={1}>Forge access</Title>
        <Text>
          Choose the automations Forge can access on behalf of an internal admin. Editors can
          prepare drafts and tests; publication remains in the Automation Editor.
        </Text>
        {context.isPending && <Text>Checking access…</Text>}
        {context.error && (
          <Alert color="red">
            Internal administrator access is required. {message(context.error)}
          </Alert>
        )}
        {context.data && (
          <>
            <Text size="sm">
              {context.data.environment} · Signed in as {context.data.email}
            </Text>
            <div className={classes.filters}>
              <StyledSelect
                styles={selectStyles}
                label="Project"
                clearable={false}
                allowDeselect={false}
                disabled={editing}
                searchable
                value={project}
                onChange={(v) => setProject(v ?? '')}
                data={mergeChoices(
                  projects,
                  selectedProject?.id === project
                    ? [selectedProject]
                    : routeProject && !projects.some((p) => p.id === routeProject)
                      ? [{ id: routeProject, name: routeProject }]
                      : [],
                ).map((p) => ({
                  value: p.id,
                  label: `${p.org_name || 'Organization unavailable'} · ${p.name || 'Unnamed project'}`,
                }))}
              />
              <StyledSelect
                styles={selectStyles}
                label="Teammate"
                clearable={false}
                allowDeselect={false}
                disabled={editing}
                searchable
                value={subject}
                onChange={(v) => setSubject(v ?? '')}
                data={[
                  { id: context.data.user_id, email: `${context.data.email} (you)` },
                  ...users.filter((u) => u.id !== context.data?.user_id),
                ].map((u) => ({ value: u.id, label: u.email ?? u.name ?? u.id }))}
              />
              <StyledButton
                variant="outline"
                className={`${classes.secondary} ${classes.loadMore}`}
                loading={loading}
                disabled={projectOffset === null && userPage === null}
                onClick={() => void loadChoices()}
              >
                Load more projects and teammates
              </StyledButton>
            </div>
            {error && <Alert color="red">{error}</Alert>}
            {project && subject && (
              <ProjectAccess
                key={`${context.data.user_id}:${context.data.environment}:${project}:${subject}`}
                project={project}
                subject={subject}
                caller={context.data.user_id}
                environment={context.data.environment}
                onEditing={setEditing}
                onProject={setSelectedProject}
              />
            )}
          </>
        )}
      </Stack>
    </Container>
  );
}

function ProjectAccess({
  project,
  subject,
  caller,
  environment,
  onEditing,
  onProject,
}: {
  project: string;
  subject: string;
  caller: string;
  environment: string;
  onEditing: (editing: boolean) => void;
  onProject: (project: Choice) => void;
}) {
  const api = useApiClient();
  const [offset, setOffset] = useState(0);
  const [edits, setEdits] = useState<Record<string, Edit>>({});
  const [saving, setSaving] = useState(false);
  const [reloading, setReloading] = useState(false);
  const busy = saving || reloading;
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const path = `/internal/forge-access/projects/${encodeURIComponent(project)}/users/${encodeURIComponent(subject)}`;
  const query = useQuery({
    meta: { suppressGlobalErrorToast: true },
    queryKey: ['forge-access', clientIdentity(api), caller, environment, project, subject, offset],
    queryFn: async ({ signal }) =>
      (
        await api.get<{ project: Choice; results: AccessRow[]; next_offset: number | null }>(path, {
          ...options,
          signal,
          params: { offset },
        })
      ).data,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const dirty = Object.keys(edits).length > 0;
  const accessUnavailable = Boolean(error || query.error);
  useEffect(() => {
    if (query.data?.project.id) onProject(query.data.project);
  }, [query.data?.project, onProject]);
  useEffect(() => {
    onEditing(dirty || busy);
    return () => onEditing(false);
  }, [dirty, busy, onEditing]);
  // Keep attempted values after an uncertain save. No automatic mutation retry.
  async function save() {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await api.patch(
        path,
        {
          changes: Object.entries(edits).map(([id, edit]) => ({
            automation_id: id,
            role: edit.role,
            expected_revision: edit.revision,
          })),
        },
        options,
      );
      const fresh = await query.refetch();
      if (fresh.error || !fresh.data) {
        setError(
          'Access was saved, but current permissions could not be reloaded. Reload current access before continuing.',
        );
      } else {
        setEdits({});
        setSaved(true);
      }
    } catch (err) {
      setError(
        `Save was not confirmed. Reload current access and review before saving again. ${message(err)}`,
      );
    } finally {
      setSaving(false);
    }
  }
  async function reload() {
    setReloading(true);
    setSaved(false);
    try {
      const fresh = await query.refetch();
      if (fresh.data && !fresh.error) {
        // Show the actual server state. A conflicting change needs a new choice.
        setEdits({});
        setError('');
      } else {
        setError('Current access could not be reloaded. Retry before editing or saving.');
      }
    } catch {
      setError('Current access could not be reloaded. Retry before editing or saving.');
    } finally {
      setReloading(false);
    }
  }
  return (
    <Stack>
      {query.error && <Alert color="red">{message(query.error)}</Alert>}
      {query.isPending && <Text>Loading automation access…</Text>}
      {query.data && (
        <>
          <Title order={2}>{query.data.project.name}</Title>
          <Text size="sm">
            Organization: {query.data.project.org_name || 'Organization unavailable'}
          </Text>
          <Text size="sm">
            No access takes effect on subsequent Forge requests. To offboard a teammate who can
            manage their own scope, remove their internal-admin membership or disable their account.
          </Text>
          <Text size="sm">
            Save changes or reload current access to discard edits before selecting another project
            or teammate.
          </Text>
          {query.data.results.length === 0 ? (
            <Text>No automations in this project.</Text>
          ) : (
            <Table.ScrollContainer minWidth={360}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Automation</Table.Th>
                    <Table.Th>Forge permission</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {query.data.results.map((row) => (
                    <Table.Tr key={row.id}>
                      <Table.Td>{row.name}</Table.Td>
                      <Table.Td className={classes.permissionColumn}>
                        <StyledSelect
                          styles={selectStyles}
                          clearable={false}
                          aria-label={`Forge access for ${row.name}`}
                          allowDeselect={false}
                          disabled={busy || accessUnavailable}
                          value={edits[row.id]?.role ?? row.role}
                          data={[
                            { value: 'none', label: 'No access' },
                            { value: 'viewer', label: 'Viewer' },
                            { value: 'editor', label: 'Editor' },
                          ]}
                          onChange={(value) => {
                            if (value === null) return;
                            setSaved(false);
                            setEdits((old) => {
                              const next = { ...old };
                              if (value === row.role) delete next[row.id];
                              else
                                next[row.id] = {
                                  role: value as Role,
                                  revision: old[row.id]?.revision ?? row.revision,
                                };
                              return next;
                            });
                          }}
                        />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
          {error && <Alert color="red">{error}</Alert>}
          {saved && <Alert color="green">Access saved.</Alert>}
          <Group>
            <StyledButton
              variant="primary"
              disabled={!dirty || busy || accessUnavailable}
              loading={saving}
              onClick={() => void save()}
            >
              Save changes
            </StyledButton>
            <StyledButton
              variant="outline"
              className={classes.secondary}
              disabled={busy}
              loading={reloading}
              onClick={() => void reload()}
            >
              Reload current access
            </StyledButton>
            <StyledButton
              variant="subtle"
              className={classes.secondary}
              disabled={!dirty || busy}
              onClick={() => {
                setEdits({});
                setError('');
              }}
            >
              Discard changes
            </StyledButton>
            <StyledButton
              variant="subtle"
              className={classes.secondary}
              disabled={dirty || busy || offset === 0}
              onClick={() => setOffset(Math.max(0, offset - 50))}
            >
              Previous
            </StyledButton>
            <StyledButton
              variant="subtle"
              className={classes.secondary}
              disabled={dirty || busy || query.data.next_offset === null}
              onClick={() => setOffset(query.data!.next_offset!)}
            >
              Next
            </StyledButton>
          </Group>
        </>
      )}
    </Stack>
  );
}
